import { AccountCircleOutlined, PhoneOutlined } from '@mui/icons-material'
import { Box, Stack, Typography } from '@mui/material'
import { CORAL_600, GRAY_400, GRAY_700, GRAY_800, GRAY_900 } from 'constants/styling/theme'
import { RescheduleReason, RescheduleStep, freeRescheduleTimeLimit, useRescheduleOrderModule } from './RescheduleOrder.context'
import { Trans, useTranslation } from 'react-i18next'
import { supportEmail, supportEmailHref } from 'constants/contacts'

import { BKBNThemeWrapper } from '../BKBNThemeWrapper'
import { BinaryButtonSwitch } from '../BinaryButtonSwitch'
import { BorderBoxWrapper } from '../BorderBoxWrapper'
import { Label } from '../Label'
import { MUIDatePicker } from '../MUIDatePicker'
import { MUIInputField } from '../MUIInputField'
import { MUIRadio } from '../MUIRadio'
import { SegmentedProgressBar } from '../SegmentedProgressBar'
import { SystemMessage } from '../SystemMessage'
import { useDynamicNow } from 'utils/hooks'
import { useMemo } from 'react'
import { useTimezone } from 'components/contexts/timezone.context'

export const RescheduleOrderController = () => {

  const { t } = useTranslation(['reschedule_order', 'enums', 'time_translations'])
  const { userTimezone } = useTimezone()
  const now = useDynamicNow(userTimezone)

  const {
    step,
    visualProgress,
    assignedCT,
    orderData,
    hoursBeforeEnd,
    isBeforeLimit,
    isPenaltyRequired,
    isMin48Hours,
    minOrderDateTime,
    maxOrderDateTime,
    newDate,
    creativeAgreed,
    rescheduleReason,
    rescheduleReasonComment,
    isCommentError,
    dateError,
    setCreativeAgreed,
    setNewDate,
    setRescheduleReason,
    setRescheduleReasonComment,
  } = useRescheduleOrderModule()

  const availableReasons = useMemo(() => Object.values(RescheduleReason).filter((reason) => reason !== RescheduleReason.REQUESTED_BY_CT), []) // Always filter until penalty responsibility shift to CT slice is implemented

  return (
    <BKBNThemeWrapper>
      <Stack gap={2}>

        {step === RescheduleStep.FORM &&
          <>

            <Typography variant="text-sm">
              <Trans t={t} i18nKey="flow_title" values={{ id: orderData?.id }}>
                &nbsp;
              </Trans>
            </Typography>

            <Stack gap={.5}>
              <SegmentedProgressBar
                progress={visualProgress}
                segments={[
                  {
                    end: 75,
                    keystone: {
                      label: `${freeRescheduleTimeLimit}${t('time_translations:hr')}`,
                      activeColor: 'coral',
                    },
                    color: 'green',
                  },
                  {
                    end: 100,
                    color: 'coral',
                  },
                ]}
              />

              {!assignedCT &&
                <Typography variant="text-sm" color={GRAY_700}>
                  <Trans t={t} i18nKey="timeline_text.no_ct">
                    &nbsp;
                  </Trans>
                </Typography>
              }

              {!!assignedCT &&
                <Typography variant="text-sm" color={isBeforeLimit ? GRAY_700 : CORAL_600}>

                  {hoursBeforeEnd > 24 && (
                    <Trans t={t} i18nKey="timeline_text.before_limit">
                      &nbsp;
                    </Trans>
                  )}

                  {hoursBeforeEnd < 24 && (
                    <Trans t={t} i18nKey="timeline_text.past_limit" values={{
                      hoursBeforeEnd,
                      supportEmail,
                    }}>
                      <a href={supportEmailHref()}>&nbsp;</a>
                    </Trans>
                  )}

                </Typography>
              }

            </Stack>

            {/* Call CT notice */}
            {!!assignedCT &&
              <SystemMessage
                variant="warning"
                title={t('call_ct_title')}
                message={
                  <Stack gap={1}>

                    <Typography variant="text-sm">
                      {t('call_ct_notice')}
                    </Typography>

                    <Stack direction="row" gap={1} alignItems="center">

                      <Stack direction="row" gap={.5} alignItems="center">
                        <AccountCircleOutlined fontSize="medium" />
                        <Typography variant="text-sm">
                          {assignedCT.name || '-'}
                        </Typography>
                      </Stack>

                      <Stack direction="row" gap={.5} alignItems="center">
                        <PhoneOutlined fontSize="medium" />
                        <Typography variant="text-sm">
                          {assignedCT.phone || '-'}
                        </Typography>
                      </Stack>

                    </Stack>

                  </Stack>
                }
              />
            }

            {/* Reason select */}
            <Stack gap={.75} alignItems="flex-start">

              <Label text={t('reason_label')} />

              {availableReasons.map((reason) => (
                <MUIRadio
                  key={reason}
                  checked={rescheduleReason === reason}
                  label={t(`enums:reschedule_order_reason.${reason}`)}
                  onChange={_ => setRescheduleReason(reason)}
                />
              ))}

              {rescheduleReason === RescheduleReason.OTHER &&
                <Box width="100%">
                  <MUIInputField
                    isMultiline
                    required
                    isError={isCommentError}
                    label={t('details_label')}
                    value={rescheduleReasonComment}
                    onChange={(e) => setRescheduleReasonComment(e.target.value)}
                  />
                </Box>
              }

              {!!assignedCT && isPenaltyRequired &&
                <Typography variant="text-xs">
                  <Trans t={t} i18nKey="requested_by_ct_info">
                    <a href={supportEmailHref()} style={{ color: GRAY_800 }}>&nbsp;</a>
                  </Trans>
                </Typography>
              }

            </Stack>

            <BorderBoxWrapper elevation="none" border={`1px solid ${GRAY_400}`} p={1.5}>
              <Stack gap={2}>

                {!!assignedCT &&
                  <Stack gap={1}>

                    <Typography variant="text-sm" fontWeight={600} color={GRAY_900}>
                      {t('ct_agreed_label')}
                    </Typography>

                    <BinaryButtonSwitch
                      onChange={(value) => setCreativeAgreed(value)}
                      value={creativeAgreed}
                      preventUnselect
                    />

                  </Stack>
                }

                <Stack gap={1}>

                  <Label
                    text={(
                      <Trans
                        t={t}
                        i18nKey={!isMin48Hours
                          ? 'date_label'
                          : !!assignedCT
                            ? 'date_label_48h'
                            : 'date_label_48h_no_ct'
                        }
                      >
                        &nbsp;
                      </Trans>
                    )}
                  />

                  <MUIDatePicker
                    defaultValue={null}
                    value={newDate}
                    onChange={(date) => setNewDate(date)}
                    minDateTime={isMin48Hours ? minOrderDateTime : now}
                    maxDate={maxOrderDateTime}
                    timeSteps={{ minutes: 15 }}
                    timezone={userTimezone}
                    disablePast
                    format='DD/MM/YYYY HH:mm'
                    errorText={!!dateError ? t(`date_error.${dateError}`) : undefined}
                    isError={!!dateError}
                    ampm={false}
                    slotProps={{
                      popper: {
                        placement: 'auto' // There is a weird behavior with the popper placement if it's not set
                      }
                    }}
                  />

                </Stack>

              </Stack>
            </BorderBoxWrapper>

          </>
        }

        {step === RescheduleStep.CONFIRM &&
          <>

            <SystemMessage
              variant="warning"
              title={t('confirm_title', { id: orderData?.id })}
              message={t('confirm_question')}
            />

            <BorderBoxWrapper p={1.5} elevation="none" border={`1px solid ${GRAY_400}`}>
              <Stack gap={1}>

                <Label text={t('confirm_new_date_label')} />

                <MUIDatePicker
                  disabled
                  defaultValue={null}
                  value={newDate}
                  timeSteps={{ minutes: 15 }}
                  timezone={userTimezone}
                  disablePast
                  maxDate={maxOrderDateTime}
                  format='DD/MM/YYYY HH:mm'
                  ampm={false}
                  slotProps={{
                    popper: {
                      placement: 'auto' // There is a weird behavior with the popper placement if it's not set
                    }
                  }}
                />

              </Stack>
            </BorderBoxWrapper>

          </>
        }

      </Stack>
    </BKBNThemeWrapper>
  )
}
